import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  Search,
  ShoppingBag,
  ArrowRight,
  ShieldCheck,
  Truck,
  RotateCcw,
  Menu,
  X,
  Minus,
  Plus,
  LockKeyhole,
} from "lucide-react";
import axios from "axios";
import "@/App.css";
import PolicyPage from "@/PolicyPage";
import AdminPage, { AuthCallback } from "@/AdminPage";
import { payWithCashfree } from "@/payments";
import PaymentReturn from "@/PaymentReturn";
import ProductPage from "@/ProductPage";
import RequestTyrePage, { RequestTyreCta } from "@/RequestTyre";
import SupportPage, { SupportSection } from "@/Support";
import TrackOrderPage from "@/TrackOrder";
import { StoreProvider, Price, RateNote, money, inr, useStore } from "@/pricing";
import { Toaster } from "@/components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hero = "https://images.unsplash.com/photo-1704325053060-b20a3a45983a?auto=format&fit=crop&w=1800&q=90";
function useCart() {
  const [cart, setCart] = useState(() => JSON.parse(localStorage.getItem("ttc-cart") || "[]"));
  useEffect(() => localStorage.setItem("ttc-cart", JSON.stringify(cart)), [cart]);
  const add = (p, v) =>
    setCart((c) => {
      const found = c.find((x) => x.product_id === p.id && x.sku === v.sku);
      return found
        ? c.map((x) => (x === found ? { ...x, quantity: x.quantity + 1 } : x))
        : [...c, { product_id: p.id, sku: v.sku, variant: v.size, quantity: 1, name: p.name, image: p.image, price_usd: v.price_usd }];
    });
  return { cart, setCart, add, count: cart.reduce((a, x) => a + x.quantity, 0) };
}
function Header({ cartCount }) {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <div className="nav-wrap">
        <Link to="/" className="brand" data-testid="site-logo">
          <span>
            <b className="brand-name">THE TYRE CO.</b>
            <br />
            JUST THE RIGHT TYRE.
          </span>
        </Link>
        <nav className={open ? "nav-links open" : "nav-links"}>
          <Link to="/" data-testid="nav-home">
            Home
          </Link>
          <Link to="/catalog/tires" data-testid="nav-tires">
            Tires
          </Link>
          <Link to="/request-tyre" data-testid="nav-request-tyre">
            Request a tyre
          </Link>
          <Link to="/track-order" data-testid="nav-track-order">
            Track order
          </Link>
          <Link to="/support" data-testid="nav-support">
            Support
          </Link>
        </nav>
        <div className="nav-actions">
          <Link to="/catalog/tires" className="icon-btn" data-testid="nav-search">
            <Search size={18} />
          </Link>
          <Link to="/cart" className="cart-link" data-testid="nav-cart">
            <ShoppingBag size={19} />
            <span>{cartCount}</span>
          </Link>
          <button className="menu-btn" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
    </header>
  );
}
function Home() {
  return (
    <>
      <section
        className="hero"
        style={{ backgroundImage: `linear-gradient(90deg,rgba(8,9,10,.92) 0%,rgba(8,9,10,.55) 46%,rgba(8,9,10,.1) 100%),url(${hero})` }}
      >
        <div className="hero-content">
          <p className="eyebrow">PRECISION / PERFORMANCE / PURPOSE</p>
          <h1>
            Performance
            <br />
            <em>starts at the road.</em>
          </h1>
          <p className="hero-copy">Premium performance tyres, sized for your vehicle. Can’t see yours? We’ll source it.</p>
          <div className="hero-actions">
            <Link to="/catalog/tires" className="btn btn-red" data-testid="hero-shop-tires">
              Shop tires <ArrowRight size={17} />
            </Link>
            <Link to="/request-tyre" className="btn btn-ghost" data-testid="hero-request-tyre">
              Request a tyre
            </Link>
          </div>
        </div>
        <div className="hero-meta">
          <span>CURATED PERFORMANCE GOODS</span>
          <span>EST. 2024 / GLOBAL DELIVERY</span>
        </div>
      </section>
      <Featured />
      <section className="category-band">
        <div className="section-intro">
          <p className="eyebrow red">SHOP BY PURPOSE</p>
          <h2>
            Built for the
            <br />
            <i>next corner.</i>
          </h2>
        </div>
        <Link to="/catalog/tires" className="category-tile tire-tile" data-testid="category-tires">
          <div>
            <span>01 / TIRES</span>
            <h3>Find your grip</h3>
            <b>
              Shop tires <ArrowRight size={16} />
            </b>
          </div>
        </Link>
        <Link to="/request-tyre" className="category-tile request-tile" data-testid="category-request-tyre">
          <div>
            <span>02 / ON REQUEST</span>
            <h3>Can’t find your size?</h3>
            <b>
              Request a tyre <ArrowRight size={16} />
            </b>
          </div>
        </Link>
      </section>
      <section className="trust-strip">
        <div>
          <Truck />
          <h3>Built to arrive</h3>
          <p>Careful packing and tracked delivery.</p>
        </div>
        <div>
          <RotateCcw />
          <h3>Human support</h3>
          <p>
            Questions? <a href="#support">Send us a message</a> and we’ll reply by email.
          </p>
        </div>
      </section>
      <section className="faq">
        <div>
          <p className="eyebrow red">THE TYRE CO</p>
          <h2>
            By the enthusiast
            <br />
            <i>for the enthusiast.</i>
          </h2>
        </div>
        <RequestTyreCta testId="home-request-tyre" />
      </section>
      <SupportSection />
    </>
  );
}
const inStock = (p) => p.variants.filter((v) => v.stock > 0);
const sizeOrder = (a, b) => (parseInt(a.split("R")[1]) || 0) - (parseInt(b.split("R")[1]) || 0) || a.localeCompare(b);
function Featured() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    const load = () => axios.get(`${API}/products`, { params: { featured: true } }).then((r) => setItems(r.data)).catch(() => {});
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, []);
  if (!items.length) return null;
  return (
    <section className="products-section" data-testid="featured-section">
      <div className="section-head">
        <div>
          <p className="eyebrow red">FEATURED / IN STOCK</p>
          <h2>Picked for the road.</h2>
        </div>
        <Link to="/catalog/tires" className="text-link" data-testid="featured-view-all">
          View all tyres <ArrowRight size={15} />
        </Link>
      </div>
      <div className="product-grid">
        {items.slice(0, 8).map((p) => (
          <ProductCard p={p} key={p.id} />
        ))}
      </div>
    </section>
  );
}
function ProductCard({ p, size }) {
  const sizes = inStock(p);
  const picked = sizes.find((v) => v.size === size);
  const prices = sizes.map((v) => v.price_usd);
  return (
    <Link to={`/product/${p.id}${picked ? `?sku=${encodeURIComponent(picked.sku)}` : ""}`} className="product-card" data-testid={`product-card-${p.id}`}>
      <div className="product-image">
        <img src={p.image} alt={`${p.brand} ${p.name}`} />
        <span>TYRE</span>
      </div>
      <div className="product-info">
        <p>{p.brand}</p>
        <h3>{p.name}</h3>
        <div>
          {picked ? (
            <Price usd={picked.price_usd} testId={`product-card-price-${p.id}`} />
          ) : (
            <Price usd={Math.min(...prices)} from={Math.max(...prices) > Math.min(...prices)} testId={`product-card-price-${p.id}`} />
          )}
          <small>{picked ? `${picked.stock} in stock` : `${sizes.length} size${sizes.length === 1 ? "" : "s"} in stock`}</small>
        </div>
        <ul className="card-sizes" data-testid={`product-card-sizes-${p.id}`}>
          {sizes.map((v) => (
            <li key={v.sku} className={v.size === size ? "active" : ""}>
              {v.size}
            </li>
          ))}
        </ul>
      </div>
    </Link>
  );
}
function SizeSlider({ sizes, value, onChange }) {
  return (
    <div className="size-slider" data-testid="catalog-size-slider">
      <span className="size-slider-label">Size</span>
      <div className="size-slider-track">
        {["", ...sizes].map((s) => (
          <button key={s || "all"} className={s === value ? "active" : ""} onClick={() => onChange(s)} data-testid={`catalog-size-${s ? s.replace(/\W+/g, "-") : "all"}`}>
            {s || "All sizes"}
          </button>
        ))}
      </div>
    </div>
  );
}
function Catalog() {
  const [products, setProducts] = useState(null);
  const [search, setSearch] = useState("");
  const [size, setSize] = useState("");
  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data.filter((p) => inStock(p).length)));
  }, []);
  if (products && !products.length)
    return (
      <main className="catalog-page">
        <div className="catalog-hero">
          <p className="eyebrow red">COLLECTION / TYRES</p>
          <h1>New stock arriving soon.</h1>
          <p>We’re loading our tyre range. Need something now? Tell us the size and we’ll source it.</p>
        </div>
        <RequestTyreCta testId="catalog-empty-request" />
      </main>
    );
  if (!products) return <div className="loading">Loading tyres...</div>;
  const sizes = [...new Set(products.flatMap((p) => inStock(p).map((v) => v.size)))].sort(sizeOrder);
  const visible = products.filter(
    (p) =>
      (!size || inStock(p).some((v) => v.size === size)) &&
      `${p.brand} ${p.name} ${p.variants.map((v) => v.size).join(" ")}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <main className="catalog-page">
      <div className="catalog-hero">
        <p className="eyebrow red">COLLECTION / TYRES</p>
        <h1>Performance tyres</h1>
        <p>The contact patch is where everything starts.</p>
      </div>
      <div className="catalog-toolbar">
        <span>{visible.length} tyre{visible.length === 1 ? "" : "s"} in stock</span>
        <label className="search-box">
          <Search size={17} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search brand, model or size"
            data-testid="catalog-search-tires"
          />
        </label>
      </div>
      <SizeSlider sizes={sizes} value={size} onChange={setSize} />
      <RateNote />
      <div className="catalog-grid">
        {visible.map((p) => (
          <ProductCard p={p} size={size} key={p.id} />
        ))}
      </div>
      {!visible.length && products.length > 0 && (
        <p className="catalog-empty" data-testid="catalog-no-results">
          No tyres in stock match {[size, search].filter(Boolean).join(" · ")}.
        </p>
      )}
      <RequestTyreCta size={size || search} testId="catalog-request-tyre" />
    </main>
  );
}
function useQuote(cart) {
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const { rate } = useStore();
  useEffect(() => {
    if (!cart.length) return;
    axios
      .post(`${API}/checkout/quote`, { items: cart })
      .then((r) => {
        setQuote(r.data);
        setError("");
      })
      .catch((e) => {
        setQuote(null);
        setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "We couldn't price your cart. Please try again.");
      });
  }, [cart, rate]);
  return { quote, error };
}
function SummaryRows({ quote }) {
  return (
    <>
      <div>
        <span>Products · USD</span>
        <b className="stacked" data-testid="summary-subtotal">
          {money(quote.subtotal_usd)}
          <small>Approx. {inr(quote.product_total_inr)}</small>
        </b>
      </div>
      <div>
        <span>Shipping</span>
        <b className="stacked" data-testid="summary-shipping">
          {inr(quote.shipping_inr)}
          <small>≈ {money(quote.shipping_inr / quote.exchange_rate)}</small>
        </b>
      </div>
      <div>
        <span>Exchange rate</span>
        <b>₹{Number(quote.exchange_rate).toFixed(2)} / $1</b>
      </div>
      <div className="summary-total">
        <span>Total payable · INR</span>
        <b data-testid="summary-total-inr">{inr(quote.total_inr)}</b>
      </div>
      <RateNote rate={quote.exchange_rate} updatedAt={quote.rate_updated_at} />
    </>
  );
}
function Cart({ cart, setCart }) {
  const { quote, error } = useQuote(cart);
  const nav = useNavigate();
  if (!cart.length)
    return (
      <main className="empty-state">
        <ShoppingBag size={40} />
        <h1>Your cart is quiet.</h1>
        <p>Start with the parts that make the difference.</p>
        <Link to="/catalog/tires" className="btn btn-red" data-testid="empty-cart-shop">
          Shop the collection
        </Link>
      </main>
    );
  return (
    <main className="cart-page">
      <div>
        <p className="eyebrow red">YOUR BUILD</p>
        <h1>Cart</h1>
        {cart.map((x, i) => (
          <div className="cart-line" key={`${x.product_id}-${x.variant}`}>
            <img src={x.image} alt="" />
            <div>
              <h3>{x.name}</h3>
              <p data-testid={`cart-size-${i}`}>
                {x.variant}
                {x.sku ? ` · ${x.sku}` : ""}
              </p>
              <Price usd={quote?.lines[i]?.unit_price_usd ?? x.price_usd} testId={`cart-price-${i}`} />
            </div>
            <div className="quantity">
              <button
                onClick={() => setCart((c) => c.map((y, j) => (j === i ? { ...y, quantity: Math.max(1, y.quantity - 1) } : y)))}
                data-testid={`cart-minus-${i}`}
              >
                <Minus size={15} />
              </button>
              <span>{x.quantity}</span>
              <button
                onClick={() => setCart((c) => c.map((y, j) => (j === i ? { ...y, quantity: y.quantity + 1 } : y)))}
                data-testid={`cart-plus-${i}`}
              >
                <Plus size={15} />
              </button>
            </div>
            <button className="remove" onClick={() => setCart((c) => c.filter((_, j) => j !== i))} data-testid={`cart-remove-${i}`}>
              Remove
            </button>
          </div>
        ))}
      </div>
      <aside className="summary">
        <p className="eyebrow">ORDER SUMMARY</p>
        <h2>Ready when you are.</h2>
        {error && (
          <p className="form-error" data-testid="cart-quote-error">
            {error}
          </p>
        )}
        {quote && (
          <>
            <SummaryRows quote={quote} />
            <button className="btn btn-red full" onClick={() => nav("/checkout")} data-testid="cart-checkout-button">
              Continue to checkout <ArrowRight size={17} />
            </button>
          </>
        )}
      </aside>
    </main>
  );
}
function Checkout({ cart, setCart }) {
  const { quote, error: quoteError } = useQuote(cart);
  const [pending, setPending] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [paying, setPaying] = useState(false);
  const [form, setForm] = useState(() => JSON.parse(sessionStorage.getItem("ttc-checkout") || '{"name":"","email":"","phone":"","address":""}'));
  useEffect(() => sessionStorage.setItem("ttc-checkout", JSON.stringify(form)), [form]);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (paying) return;
    setError("");
    setPaying(true);
    let handedOff = false;
    try {
      const key = JSON.stringify({ cart, form, total: quote?.total_inr });
      const created = pending?.key === key ? pending.order : (await axios.post(`${API}/orders`, { items: cart, customer: form, accepted_terms: agreed })).data;
      if (!created.payment_session_id) {
        setOrder(created);
        return;
      }
      setPending({ key, order: created });
      await payWithCashfree(created, {
        onError: (msg) => {
          setPaying(false);
          setError(msg);
        },
      });
      handedOff = true;
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(
        typeof d === "string"
          ? d
          : Array.isArray(d)
            ? `Please check ${d[0]?.loc?.slice(-1)[0] || "your details"}: ${d[0]?.msg}`
            : err.message || "Please check your details and try again.",
      );
    } finally {
      if (!handedOff) setPaying(false);
    }
  };
  if (!cart.length && !order) return <Navigate to="/cart" replace />;
  if (order)
    return (
      <main className="confirmation">
        <div className="confirm-icon">
          <ShieldCheck />
        </div>
        <p className="eyebrow red">ORDER {order.order_id}</p>
        {order.payment_status === "paid" ? (
          <>
            <h1>
              Payment
              <br />
              <i>confirmed.</i>
            </h1>
            <p data-testid="confirmation-paid">
              Thank you. We received {inr(order.total_inr)} and will email {order.customer?.email} when your order ships. Keep your order
              number <b>{order.order_id}</b> to <Link to="/track-order">track your order</Link>.
            </p>
          </>
        ) : (
          <>
            <h1>
              Your build is
              <br />
              <i>almost moving.</i>
            </h1>
            <p>
              We’ve saved your order details. Payment is currently paused until online payments are configured by the store
              administrator.
            </p>
            <div className="payment-paused">
              <LockKeyhole size={18} />
              <div>
                <b>Payment setup required</b>
                <span>
                  INR total calculated and held at {inr(order.total_inr)} · Rate ₹{order.exchange_rate}/USD
                </span>
              </div>
            </div>
          </>
        )}
        <Link to="/" className="btn btn-outline" data-testid="confirmation-home-button">
          Return home
        </Link>
      </main>
    );
  return (
    <main className="checkout-page">
      <div className="checkout-main">
        <p className="eyebrow red">CHECKOUT / GUEST</p>
        <h1>
          Finish your
          <br />
          <i>configuration.</i>
        </h1>
        <form onSubmit={submit}>
          <label>
            Full name
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" />
          </label>
          <label>
            Email
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="checkout-email"
            />
          </label>
          <label>
            Phone
            <input
              required
              type="tel"
              pattern="(\+91 ?)?[6-9][0-9]{9}"
              title="Enter a 10-digit Indian mobile number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="checkout-phone"
            />
          </label>
          <label>
            Delivery address
            <textarea
              required
              minLength={5}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              data-testid="checkout-address"
            />
          </label>
          <label className="consent-check">
            <input type="checkbox" required checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="checkout-terms-checkbox" />
            <span>
              I agree to the{" "}
              <Link to="/policies/terms" target="_blank" rel="noopener noreferrer" data-testid="checkout-terms-link">
                Terms &amp; Conditions
              </Link>{" "}
              and{" "}
              <Link to="/policies/returns" target="_blank" rel="noopener noreferrer" data-testid="checkout-refund-link">
                Refund &amp; Cancellation Policy
              </Link>
              , including that refunds are only for manufacturing defects after inspection.
            </span>
          </label>
          {(error || quoteError) && (
            <p className="form-error" data-testid="checkout-error">
              {error || quoteError}
            </p>
          )}
          <button className="btn btn-red full" disabled={!quote || paying} data-testid="checkout-pay-button">
            {paying ? "Opening payment…" : `Pay ${quote ? inr(quote.total_inr) : "INR"}`} <ArrowRight size={17} />
          </button>
        </form>
      </div>
      <aside className="checkout-side">
        {!quote ? <p className="loading-inline" data-testid="checkout-payment-loading">Preparing secure payment…</p> : <>
        <p className="eyebrow">{quote?.payment_ready ? "SECURE PAYMENT" : "PAYMENT SETUP"}</p>
        <h2>{quote?.payment_ready ? "Pay securely with Cashfree." : "Online payments are being set up."}</h2>
        <p>
          {quote?.payment_ready
            ? "Cards, UPI, netbanking and wallets. Your total is charged in INR."
            : "This checkout validates inventory, calculates the INR amount on the server, and will hand off to Cashfree after test credentials are configured."}
        </p>
        <div className="setup-status" data-testid="checkout-payment-status">
          <span className={quote?.payment_ready ? "status-dot" : "status-dot warn"} />{" "}
          {quote?.payment_ready ? "Cashfree connected" : "Payment gateway not configured"}
        </div></>}
        {quote && (
          <div className="checkout-total">
            <span>Final payable</span>
            <b>{inr(quote.total_inr)}</b>
            <small data-testid="checkout-total-breakdown">
              {money(quote.subtotal_usd)} at ₹{Number(quote.exchange_rate).toFixed(2)}/USD + {inr(quote.shipping_inr)} shipping (≈{" "}
              {money(quote.shipping_inr / quote.exchange_rate)})
            </small>
          </div>
        )}
      </aside>
    </main>
  );
}
function AppRoutes({ cartState }) {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/catalog/tires" element={<Catalog />} />
      <Route path="/catalog/*" element={<Navigate to="/catalog/tires" replace />} />
      <Route path="/request-tyre" element={<RequestTyrePage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/track-order" element={<TrackOrderPage />} />
      <Route path="/payment/return" element={<PaymentReturn setCart={cartState.setCart} />} />
      <Route path="/product/:id" element={<ProductPage add={cartState.add} />} />
      <Route path="/cart" element={<Cart {...cartState} />} />
      <Route path="/checkout" element={<Checkout cart={cartState.cart} setCart={cartState.setCart} />} />
      <Route path="/admin" element={<AdminPage location={location} />} />
      <Route path="/policies/:slug" element={<PolicyPage />} />
    </Routes>
  );
}
function App() {
  const cartState = useCart();
  return (
    <BrowserRouter>
      <StoreProvider>
        <Toaster />
        <Header cartCount={cartState.count} />
        <AppRoutes cartState={cartState} />
        <footer>
          <div className="footer-brand" data-testid="footer-brand">
            THE TYRE CO.
            <br />
            <span>JUST THE RIGHT TYRE.</span>
          </div>
          <div>
            <span>CONTACT</span>
            <a href="mailto:trade1ttc@gmail.com" data-testid="footer-email">
              trade1ttc@gmail.com
            </a>
            <Link to="/support" data-testid="footer-support">
              Email support
            </Link>
            <Link to="/track-order" data-testid="footer-track-order">
              Track my order
            </Link>
          </div>
          <div>
            <span>POLICIES</span>
            <Link to="/policies/shipping" data-testid="footer-shipping">
              Shipping
            </Link>
            <Link to="/policies/returns" data-testid="footer-returns">
              Refunds & cancellation
            </Link>
            <Link to="/policies/privacy" data-testid="footer-privacy">
              Privacy
            </Link>
            <Link to="/policies/terms" data-testid="footer-terms">
              Terms
            </Link>
            <Link to="/policies/contact" data-testid="footer-contact">
              Contact us
            </Link>
          </div>
        </footer>
      </StoreProvider>
    </BrowserRouter>
  );
}
export default App;
